import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { useWaves } from "../context/WaveContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { History, ChevronDown, ChevronRight } from "lucide-react";

const ENTITY_LABELS = {
  restaurant: "Restaurante",
  event: "Evento",
  attendance: "Asistencia",
  invoice: "Factura",
  user: "Usuario",
  wave: "Wave",
  employee: "Empleado",
  employee_extra: "Extra/Dieta",
  employee_paid_period: "Mes pagado",
  budget: "Presupuesto",
};

const ACTION_COLORS = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  delete: "bg-red-50 text-red-700 border-red-200",
  status_change: "bg-violet-50 text-violet-700 border-violet-200",
  login: "bg-zinc-100 text-zinc-700 border-zinc-200",
  allow: "bg-emerald-50 text-emerald-700 border-emerald-200",
  revoke: "bg-red-50 text-red-700 border-red-200",
  import: "bg-amber-50 text-amber-700 border-amber-200",
};

const ACTION_LABELS = {
  create: "Crear",
  update: "Editar",
  delete: "Eliminar",
  status_change: "Cambio estado",
  login: "Login",
  allow: "Permitir",
  revoke: "Revocar",
  import: "Importar",
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
};

export default function Audit() {
  const { currentWave } = useWaves();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterEmail, setFilterEmail] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filterEntity !== "all") params.entity_type = filterEntity;
    if (filterAction !== "all") params.action = filterAction;
    if (filterEmail.trim()) params.user_email = filterEmail.trim();
    params.limit = 500;
    try {
      const { data } = await api.get("/audit", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterEntity, filterAction]);

  const emailOptions = useMemo(() => {
    const seen = {};
    items.forEach((i) => { if (i.user_email) seen[i.user_email] = true; });
    return Object.keys(seen).sort();
  }, [items]);

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="audit-page">
      <div className="mb-8">
        <p className="label-eyebrow">Auditoría · {currentWave?.name}</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3">
          <History className="h-8 w-8 text-zinc-700" strokeWidth={1.5} />
          Registro de cambios
        </h1>
        <p className="text-sm text-zinc-600 mt-3 max-w-2xl">Quién hizo qué y cuándo: logins, creación/edición/eliminación de restaurantes, eventos, facturas, asistencia, usuarios y permisos.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div>
          <Label className="text-xs uppercase tracking-wider">Entidad</Label>
          <Select value={filterEntity} onValueChange={setFilterEntity}>
            <SelectTrigger className="rounded-none" data-testid="audit-filter-entity"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Todas</SelectItem>
              {Object.entries(ENTITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Acción</Label>
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="rounded-none" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Todas</SelectItem>
              {Object.entries(ACTION_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider">Email del usuario</Label>
          <Input
            list="audit-emails"
            placeholder="Filtrar por email…"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            onBlur={load}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            className="rounded-none"
            data-testid="audit-filter-email"
          />
          <datalist id="audit-emails">
            {emailOptions.map((e) => <option key={e} value={e} />)}
          </datalist>
        </div>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="audit-list">
        <div className="grid grid-cols-12 px-5 py-2 bg-zinc-50 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
          <div className="col-span-2">Fecha</div>
          <div className="col-span-2">Usuario</div>
          <div className="col-span-2">Acción</div>
          <div className="col-span-2">Entidad</div>
          <div className="col-span-4">Detalle</div>
        </div>
        {loading && <div className="p-10 text-center text-sm text-zinc-500">Cargando…</div>}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="audit-empty">
            Sin registros que mostrar.
          </div>
        )}
        {!loading && items.map((it) => {
          const hasChanges = it.changes && Object.keys(it.changes).length > 0;
          const isOpen = expanded[it.id];
          return (
            <div key={it.id} className="border-b border-zinc-100" data-testid={`audit-row-${it.id}`}>
              <button
                onClick={() => hasChanges && setExpanded({ ...expanded, [it.id]: !isOpen })}
                className={`w-full grid grid-cols-12 px-5 py-3 items-center text-sm text-left ${hasChanges ? "hover:bg-zinc-50 cursor-pointer" : "cursor-default"}`}
              >
                <div className="col-span-2 font-mono text-xs text-zinc-600">{fmtTs(it.ts)}</div>
                <div className="col-span-2 text-xs">
                  <div className="font-medium truncate">{it.user_email || "—"}</div>
                  <div className="label-eyebrow text-zinc-400">{it.user_role}</div>
                </div>
                <div className="col-span-2">
                  <span className={`text-xs px-2 py-1 border uppercase tracking-wider ${ACTION_COLORS[it.action] || "bg-zinc-50 text-zinc-700 border-zinc-200"}`}>
                    {ACTION_LABELS[it.action] || it.action}
                  </span>
                </div>
                <div className="col-span-2 text-xs">
                  <div className="label-eyebrow text-zinc-400">{ENTITY_LABELS[it.entity_type] || it.entity_type}</div>
                  <div className="font-medium truncate" title={it.entity_label}>{it.entity_label || it.entity_id || "—"}</div>
                </div>
                <div className="col-span-4 text-xs text-zinc-600 flex items-center gap-2">
                  {hasChanges && (isOpen ? <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} /> : <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />)}
                  {hasChanges ? (
                    <span className="font-mono truncate">
                      {Object.keys(it.changes).slice(0, 3).join(", ")}
                      {Object.keys(it.changes).length > 3 && ` +${Object.keys(it.changes).length - 3}`}
                    </span>
                  ) : it.meta ? <span className="font-mono truncate">{JSON.stringify(it.meta)}</span> : <span className="text-zinc-400">—</span>}
                </div>
              </button>
              {hasChanges && isOpen && (
                <div className="px-5 pb-3 bg-zinc-50/50" data-testid={`audit-detail-${it.id}`}>
                  <div className="border border-zinc-200 bg-white p-3">
                    <div className="grid grid-cols-3 gap-2 text-xs label-eyebrow text-zinc-500 pb-2 border-b border-zinc-100">
                      <div>Campo</div><div>Antes</div><div>Después</div>
                    </div>
                    {Object.entries(it.changes).map(([k, v]) => (
                      <div key={k} className="grid grid-cols-3 gap-2 py-1.5 text-xs font-mono border-b border-zinc-50 last:border-0">
                        <div className="font-medium text-zinc-700">{k}</div>
                        <div className="text-red-700 truncate" title={fmtVal(v.before)}>{fmtVal(v.before)}</div>
                        <div className="text-emerald-700 truncate" title={fmtVal(v.after)}>{fmtVal(v.after)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
